import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, MessageCircle, RotateCcw, HeartHandshake } from "lucide-react";
import { API, WHATSAPP_NUMBER, formatRupiah } from "@/lib/site";
import { Reveal } from "./motion-prims";

const PRESETS = [25000, 50000, 100000, 250000, 500000];

const inputCls =
  "w-full bg-cream border-2 border-ink rounded-xl px-4 py-3 text-sm sm:text-base placeholder:text-pine/50 focus:outline-none focus:ring-4 focus:ring-gold/40 transition-shadow";

export default function DonateForm() {
  const qc = useQueryClient();
  const { data: campaigns } = useQuery({
    queryKey: ["campaigns"],
    queryFn: async () => (await axios.get(`${API}/campaigns`)).data,
  });

  const [campaignId, setCampaignId] = useState("");
  const [preset, setPreset] = useState(50000);
  const [custom, setCustom] = useState("");
  const [useCustom, setUseCustom] = useState(false);
  const [name, setName] = useState("");
  const [contact, setContact] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [receipt, setReceipt] = useState(null);

  useEffect(() => {
    const handler = (e) => {
      setCampaignId(e.detail.campaignId);
      setReceipt(null);
    };
    window.addEventListener("donate:program", handler);
    return () => window.removeEventListener("donate:program", handler);
  }, []);

  const amount = useCustom ? parseInt(custom, 10) || 0 : preset;

  const submit = async (e) => {
    e.preventDefault();
    if (!campaignId) return toast.error("Pilih program kerja dulu ya");
    if (!name.trim() || name.trim().length < 2) return toast.error("Isi nama Anda (min. 2 huruf)");
    if (!contact.trim() || contact.trim().length < 5) return toast.error("Isi nomor WA atau email yang valid");
    if (amount < 10000) return toast.error("Minimal donasi Rp 10.000");
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/donations`, {
        campaign_id: campaignId,
        name: name.trim(),
        contact: contact.trim(),
        amount,
        message: message.trim(),
      });
      setReceipt(data);
      qc.invalidateQueries({ queryKey: ["campaigns"] });
      qc.invalidateQueries({ queryKey: ["donations"] });
      toast.success("Terima kasih! Niat donasi Anda tercatat.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal mengirim. Coba lagi.");
    } finally {
      setSending(false);
    }
  };

  const reset = () => {
    setReceipt(null);
    setPreset(50000);
    setUseCustom(false);
    setCustom("");
    setMessage("");
  };

  const waReceipt = receipt
    ? `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
        `Halo BANTU DONASI BERSAMA PR! Saya ${receipt.name} sudah menyalurkan ${formatRupiah(receipt.amount)} untuk program "${receipt.campaign_title}". Ini bukti transfernya.`
      )}`
    : "#";

  return (
    <section id="donasi" className="scroll-mt-20 py-20 sm:py-28 relative overflow-hidden">
      <div className="halftone absolute inset-x-0 bottom-0 h-64 opacity-30 pointer-events-none" aria-hidden="true" />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-[0.9fr_1.1fr] gap-10 lg:gap-16 items-start">
        <Reveal>
          <p className="text-xs sm:text-sm font-bold uppercase tracking-[0.25em] text-gold-dark">Salurkan Donasi</p>
          <h2 className="mt-4 font-display font-black tracking-tight leading-tight text-3xl sm:text-4xl lg:text-5xl">
            Berapa pun nominalnya, <span className="italic text-gold-dark">dampaknya nyata.</span>
          </h2>
          <p className="mt-6 text-base sm:text-lg leading-relaxed text-pine max-w-md">
            Catat niat donasi Anda di sini, lalu selesaikan lewat transfer bank atau QRIS. Setiap catatan
            langsung muncul di dinding donatur.
          </p>
          <div className="mt-8 flex items-center gap-4 border-2 border-ink rounded-2xl bg-paper p-5 max-w-md shadow-retro-sm">
            <span className="grid place-items-center w-12 h-12 rounded-full bg-gold border-2 border-ink shrink-0">
              <HeartHandshake className="w-6 h-6" />
            </span>
            <p className="text-sm leading-relaxed text-pine">
              <span className="font-bold text-ink">Transparan sejak awal.</span> Nominal, program, dan pesan
              Anda tampil terbuka untuk menggerakkan orang lain.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          {receipt ? (
            <div
              data-testid="donation-success-modal"
              className="bg-cream border-2 border-ink rounded-2xl shadow-retro-lg p-6 sm:p-8 text-center"
            >
              <span className="mx-auto grid place-items-center w-16 h-16 rounded-full bg-gold border-2 border-ink">
                <CheckCircle2 className="w-8 h-8" />
              </span>
              <h3 className="mt-5 font-display font-black text-2xl sm:text-3xl">Terima kasih, {receipt.name}!</h3>
              <p className="mt-2 text-pine text-sm sm:text-base">
                Niat donasi <span className="font-bold text-ink">{formatRupiah(receipt.amount)}</span> untuk{" "}
                <span className="font-bold text-ink">{receipt.campaign_title}</span> sudah tercatat.
              </p>
              <p className="mt-4 text-sm text-pine bg-paper border-2 border-ink/10 rounded-xl px-4 py-3 leading-relaxed">
                Selanjutnya: lakukan transfer sesuai nominal, lalu konfirmasi lewat WhatsApp agar donasi
                terverifikasi.
              </p>
              <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
                <a
                  href={waReceipt}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="donation-success-wa-button"
                  className="btn-retro bg-gold text-ink rounded-full px-6 py-3 text-sm"
                >
                  <MessageCircle className="w-4 h-4" /> Konfirmasi via WhatsApp
                </a>
                <button
                  data-testid="donation-reset-button"
                  onClick={reset}
                  className="btn-retro bg-cream text-ink rounded-full px-6 py-3 text-sm"
                >
                  <RotateCcw className="w-4 h-4" /> Donasi Lagi
                </button>
              </div>
            </div>
          ) : (
            <form
              data-testid="donation-form"
              onSubmit={submit}
              className="bg-paper border-2 border-ink rounded-2xl shadow-retro-lg p-6 sm:p-8 flex flex-col gap-5"
            >
              <div>
                <label htmlFor="campaign" className="text-xs font-bold uppercase tracking-[0.15em] text-pine">
                  Pilih Program
                </label>
                <select
                  id="campaign"
                  data-testid="donation-campaign-select"
                  value={campaignId}
                  onChange={(e) => setCampaignId(e.target.value)}
                  className={`mt-2 ${inputCls} appearance-none ${campaignId ? "" : "text-pine/60"}`}
                >
                  <option value="" disabled>
                    — Pilih salah satu program kerja —
                  </option>
                  {(campaigns || []).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.title}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-xs font-bold uppercase tracking-[0.15em] text-pine">Nominal Donasi</label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {PRESETS.map((p) => (
                    <button
                      key={p}
                      type="button"
                      data-testid={`donation-nominal-${p}`}
                      onClick={() => {
                        setPreset(p);
                        setUseCustom(false);
                      }}
                      className={`border-2 border-ink rounded-full px-4 py-2 text-sm font-bold transition-all ${
                        !useCustom && preset === p ? "bg-gold shadow-retro-sm" : "bg-cream hover:bg-gold/30"
                      }`}
                    >
                      {formatRupiah(p)}
                    </button>
                  ))}
                  <button
                    type="button"
                    data-testid="donation-nominal-custom"
                    onClick={() => setUseCustom(true)}
                    className={`border-2 border-ink rounded-full px-4 py-2 text-sm font-bold transition-all ${
                      useCustom ? "bg-gold shadow-retro-sm" : "bg-cream hover:bg-gold/30"
                    }`}
                  >
                    Nominal Sendiri
                  </button>
                </div>
                {useCustom && (
                  <input
                    type="number"
                    min="10000"
                    data-testid="donation-custom-amount-input"
                    value={custom}
                    onChange={(e) => setCustom(e.target.value)}
                    placeholder="Minimal Rp 10.000"
                    className={`mt-3 ${inputCls}`}
                  />
                )}
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="name" className="text-xs font-bold uppercase tracking-[0.15em] text-pine">
                    Nama
                  </label>
                  <input
                    id="name"
                    data-testid="donation-name-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Nama Anda"
                    className={`mt-2 ${inputCls}`}
                  />
                </div>
                <div>
                  <label htmlFor="contact" className="text-xs font-bold uppercase tracking-[0.15em] text-pine">
                    No. WA / Email
                  </label>
                  <input
                    id="contact"
                    data-testid="donation-contact-input"
                    value={contact}
                    onChange={(e) => setContact(e.target.value)}
                    placeholder="08xx… / nama@mail.com"
                    className={`mt-2 ${inputCls}`}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="message" className="text-xs font-bold uppercase tracking-[0.15em] text-pine">
                  Pesan / Doa (opsional)
                </label>
                <textarea
                  id="message"
                  data-testid="donation-message-input"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={3}
                  placeholder="Tulis pesan atau doa untuk penerima manfaat…"
                  className={`mt-2 ${inputCls} resize-none`}
                />
              </div>

              <button
                type="submit"
                data-testid="donation-submit-button"
                disabled={sending}
                className="btn-retro bg-olive text-cream rounded-full px-6 py-4 text-base w-full disabled:opacity-60 disabled:pointer-events-none"
              >
                <HeartHandshake className="w-5 h-5" />
                {sending ? "Mengirim…" : `Salurkan ${formatRupiah(amount || 0)}`}
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
}
